import { useCallback, useEffect, useMemo, useState } from 'react';

import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { ShuffleIcon } from '@hugeicons/core-free-icons';
import {
  Archive03Icon,
  Copy01Icon,
  Copy02Icon,
  Delete02Icon,
  DragDropVerticalIcon,
  GitBranchIcon,
  PlayIcon,
  RestoreBinIcon,
  SquareArrowDownDoubleIcon,
  SquareArrowUpDoubleIcon,
  SquareLock02Icon,
} from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { FieldTypes, FieldTypesOptions } from 'constants/field-types-enum';
import { FormTypes } from 'constants/forms';
import { operatorTypes } from 'constants/logic';
import { MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import { Permissions } from 'constants/permissions';
import FeatherIcon from 'feather-icons-react';
import {
  createLogicJumpsMessage,
  getAffectedLogicJumps,
  getLogicJumpsForField,
} from 'helpers/form';
import { useAccessControl } from 'hooks/access-control';
import { Badge, Tab, Tabs } from 'react-bootstrap';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import Alert from 'components/Alert/Alert';
import { CollapsibleBlock, CollapsibleGroup } from 'components/CollapsibleBlock/CollapsibleBlock';
import { EditableSettings } from 'components/EditableSettings/EditableSettings';
import {
  Button,
  EditableTitle,
  InputGroup,
  RichTextEditor,
  Select,
  Textarea,
  ToggleSwitchInput,
} from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import { IconButton } from 'components/IconButton/IconButton';
import { StatusPill } from 'components/Pills/StatusPills';

import { messagePopUp } from 'api/app-slice';
import { selectPermissions } from 'api/user-permissions-slice';

import styles from './FormEdit.module.scss';
import { FormPreviewModal } from './FormPreviewModal/FormPreviewModal';
import { DateSettings } from './SettingsComponents/DateSettings/DateSettings';
import { DefaultSettings } from './SettingsComponents/DefaultSettings/DefaultSettings';
import { DownloadSettings } from './SettingsComponents/DownloadSettings/DownloadSettings';
import { LogicSettings } from './SettingsComponents/LogicSettings/LogicSettings';
import { MultipleChoiceSettings } from './SettingsComponents/MultipleChoiceSettings/MultipleChoiceSettings';
import { OpinionScaleSettings } from './SettingsComponents/OpinionScaleSettings/OpinionScaleSettings';
import { StarRatingSettings } from './SettingsComponents/StarRatingSettings/StarRatingSettings';

export const FormEdit = ({
  company,
  form,
  teams,
  blockBefore = 0,
  aiGenerators = false,
  readOnly = false,
  useModernForm = false,
  dataTestId,
  locale = 'en_GB',
  onCopy = () => {},
  onSave = () => {},
  onDelete = () => {},
  onClose = () => {},
  onArchive = () => {},
  onRestore = () => {},
  onJDAIGenerator = () => {},
  onAIQuestionnaireGenerator = () => {},
}) => {
  const {
    control,
    register,
    setValue,
    getValues,
    unregister,
    reset,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: form });
  const {
    fields,
    append,
    move,
    remove: removeField,
  } = useFieldArray({ control, name: 'fields', keyName: 'key' });
  const {
    fields: logicJumps,
    append: appendLogic,
    remove: removeLogic,
  } = useFieldArray({ control, name: 'logic_jumps', keyName: 'key' });

  const permissions = useSelector(selectPermissions);

  const hasAccess = useAccessControl(permissions);

  const nameValue = useWatch({ control, name: 'name' });
  const watchTeam = useWatch({ control, name: 'team_id' });
  const formValues = useWatch({ control });

  const dispatch = useDispatch();

  const [showPreview, setShowPreview] = useState(false);
  const [fieldReorderAlert, setFieldReorderAlert] = useState(false);
  const [dragInfo, setDragInfo] = useState(null);
  const [logicJumpAlertMessage, setLogicJumpAlertMessage] = useState('');
  const [pasteLogicJumpAlert, setPasteLogicJumpAlert] = useState(false);
  const [copyFieldDetails, setCopyFieldDetails] = useState(null);
  const [pasteLogicJumpFieldIndices, setPasteLogicJumpFieldIndices] = useState(null);
  const [pasteLogicJumpMessage, setPasteLogicJumpMessage] = useState('');
  const [deleteFieldAlert, setDeleteFieldAlert] = useState(false);
  const [deleteFieldMessage, setDeleteFieldMessage] = useState('');
  const [deleteFieldIndex, setDeleteFieldIndex] = useState([]);
  const [deleteFieldLogicJumps, setDeleteFieldLogicJumps] = useState([]);
  const [blockBeforeFields, setBlockBeforeFields] = useState(blockBefore);

  useEffect(() => reset(form), [form]);

  useEffect(() => {
    let updatedFields = [];
    let order = 0;

    // Read current form values to preserve properties like downloadUrl that might be set via setValue
    // Using getValues() instead of formValues to avoid dependency issues
    const currentFields = getValues('fields') || [];

    fields.forEach((field, index) => {
      order++;
      // Merge the current form value (which includes downloadUrl) with the field from useFieldArray
      // Spread field first, then currentField to preserve properties like downloadUrl that were set via setValue
      const currentField = currentFields[index] || {};
      updatedFields.push({ ...field, ...currentField, order: order });
    });

    setValue('fields', updatedFields);

    window.scrollTo(0, document.body.scrollHeight);
  }, [fields.length, getValues, setValue]);

  const addField = () => {
    append({
      order: fields.length + 1,
      text: '',
      check_part: 'CUSTOM',
      type: FieldTypes.FreeText,
      required: true,
      options: { required: true, options: [{ value: '' }] },
    });
  };

  const onDragEnd = ({ source, destination }) => {
    const currentOrder = source.index + 1;
    const targetOrder = destination.index + 1;

    const logicJumpsAttached = getAffectedLogicJumps(logicJumps, currentOrder, targetOrder);

    const message = createLogicJumpsMessage(logicJumpsAttached);

    if (logicJumpsAttached.length > 0) {
      setLogicJumpAlertMessage(message);
      setDragInfo({
        source,
        destination,
        logicJumpsAttached,
      });
      setFieldReorderAlert(true);
      return;
    } else {
      reorderFields(source, destination);
    }
  };

  const addIntroField = () => {
    append({
      order: 1,
      text: '',
      check_part: 'CUSTOM',
      type: FieldTypes.Statement,
      required: true,
      options: { required: true, intro: true, options: [{ value: '' }] },
    });
    move(fields.length, 0);
    setBlockBeforeFields(blockBeforeFields + 1);
  };

  const handleHasIntroField = useCallback(hasIntroField => {
    if (hasIntroField) {
      addIntroField();
    } else {
      removeField(0);
      setBlockBeforeFields(blockBeforeFields - 1);
    }
  }, []);

  const reorderFields = (source, destination, logicJumpsAttached = []) => {
    removeLogicJumps(logicJumpsAttached);

    if (!source || !destination || destination.index < blockBeforeFields) return;
    let first, last;

    if (source.index > destination.index) {
      first = destination.index;
      last = source.index;
    } else {
      first = source.index;
      last = destination.index;
    }

    move(source.index, destination.index);
    for (let i = first; i <= last; i++) setValue(`fields[${i}].order`, i + 1);
  };

  const removeLogicJumps = useMemo(() => {
    return logicJumpsAttached => {
      const indicesToRemove = logicJumpsAttached
        ?.map(jump => logicJumps.findIndex(logicJump => logicJump.id === jump.id))
        .filter(index => index !== -1)
        .sort((a, b) => b - a);

      indicesToRemove.forEach(index => {
        removeLogic(index);
      });
    };
  }, [logicJumps, removeLogic]);

  const handleFieldReorder = () => {
    if (dragInfo) {
      reorderFields(dragInfo.source, dragInfo.destination, dragInfo.logicJumpsAttached);
      setDragInfo(null);
    }
    setFieldReorderAlert(false);
    setLogicJumpAlertMessage('');
  };

  const handleDeleteField = index => {
    const field = fields[index];
    const logicJumpsAttached = getLogicJumpsForField(logicJumps, field.order);
    const message =
      logicJumpsAttached?.length > 0 ? createLogicJumpsMessage(logicJumpsAttached) : '';
    setDeleteFieldLogicJumps(logicJumpsAttached);
    setDeleteFieldAlert(true);
    setDeleteFieldMessage(message);
    setDeleteFieldIndex(index);
  };

  const handleDeleteFieldConfirm = () => {
    removeField(deleteFieldIndex);
    if (deleteFieldIndex === 0) {
      setBlockBeforeFields(blockBeforeFields - 1);
    }
    setDeleteFieldAlert(false);
    setDeleteFieldMessage('');
    setDeleteFieldIndex(null);
    removeLogicJumps(deleteFieldLogicJumps);
    setDeleteFieldLogicJumps([]);
  };

  const duplicateField = field => {
    const { id, ...fieldWithoutId } = field;
    const newField = { ...fieldWithoutId, order: fields.length + 1 };
    append(newField);
    dispatch(
      messagePopUp({
        text: 'Field duplicated successfully - Your field has been added to the bottom so the form logic remains intact.',
        state: MESSAGE_STATE_SUCCESS,
        hide: true,
      }),
    );
  };

  const copyField = field => {
    const { id, ...fieldWithoutId } = field;
    const newField = { ...fieldWithoutId };
    setCopyFieldDetails(newField);
  };

  const pasteField = (field, targetIndex) => {
    append(field);
    move(fields.length, targetIndex);
    setCopyFieldDetails(null);
  };

  const handlePasteLogicJump = () => {
    if (pasteLogicJumpFieldIndices) {
      const [targetIndex, targetOrder, logicJumpsAttached] = pasteLogicJumpFieldIndices;
      const newField = { ...copyFieldDetails, order: targetOrder };
      pasteField(newField, targetIndex);
      removeLogicJumps(logicJumpsAttached);
    }
    setPasteLogicJumpAlert(false);
    setPasteLogicJumpMessage('');
    setPasteLogicJumpFieldIndices(null);
  };

  const pasteFieldWithLogicCheck = (targetOrder, targetIndex) => {
    if (!copyFieldDetails) return;

    const logicJumpsAttached = getAffectedLogicJumps(logicJumps, fields.length, targetOrder);

    const message = createLogicJumpsMessage(logicJumpsAttached);

    if (logicJumpsAttached.length > 0) {
      setPasteLogicJumpAlert(true);
      setPasteLogicJumpMessage(message);
      setPasteLogicJumpFieldIndices([targetIndex, targetOrder, logicJumpsAttached]);
      return;
    }

    const newField = { ...copyFieldDetails, order: targetOrder };
    pasteField(newField, targetIndex);
  };

  const pasteFieldAbove = field => {
    pasteFieldWithLogicCheck(field.order, field.order - 1);
  };

  const pasteFieldBelow = field => {
    pasteFieldWithLogicCheck(field.order + 1, field.order);
  };

  const hasIntroField = useMemo(() => {
    return fields?.some(field => field.options?.intro);
  }, [fields]);

  const onError = error => {
    console.error('Validation Error', error);
  };

  const handleDelete = () => {
    onDelete(form);
    onClose();
  };

  const handleArchive = () => {
    onArchive();
    onClose();
  };

  const handleRestore = () => {
    onRestore();
    onClose();
  };

  const getLogicJumpsCount = order => {
    return logicJumps.filter(jump => jump.from === order || jump.to === order)?.length || 0;
  };

  return (
    <>
      <FormPreviewModal
        visible={showPreview}
        onClose={() => setShowPreview(false)}
        form={formValues}
        useModernForm={useModernForm}
      />
      <Alert
        show={fieldReorderAlert}
        title={`You have logic setup. Are you sure you want to continue?`}
        message={`The following fields will be affected by this change: <br/><br/>${logicJumpAlertMessage}`}
        ok="Confirm"
        type="error"
        onOk={handleFieldReorder}
        icon={ShuffleIcon}
        cancel="Cancel"
        onCancel={() => {
          setFieldReorderAlert(false);
          setDragInfo(null);
          setLogicJumpAlertMessage('');
        }}
      />
      <Alert
        show={pasteLogicJumpAlert}
        title={`You have logic setup. Are you sure you want to continue?`}
        message={`The following fields will be affected by this change: <br/><br/>${pasteLogicJumpMessage}`}
        ok="Confirm"
        type="error"
        onOk={handlePasteLogicJump}
        icon={ShuffleIcon}
        cancel="Cancel"
        onCancel={() => {
          setPasteLogicJumpAlert(false);
          setCopyFieldDetails(null);
        }}
      />
      <Alert
        show={deleteFieldAlert}
        title={`${deleteFieldLogicJumps?.length > 0 ? 'You have logic setup. Are you sure you want to continue?' : 'Are you sure you want to delete this field?'}`}
        message={`${deleteFieldLogicJumps?.length > 0 ? 'The following logic jumps will be removed: <br/><br/>' : ''} ${deleteFieldMessage}`}
        ok="Confirm"
        type="error"
        onOk={handleDeleteFieldConfirm}
        icon={ShuffleIcon}
        cancel="Cancel"
        onCancel={() => {
          setDeleteFieldAlert(false);
          setDeleteFieldMessage('');
          setDeleteFieldIndex(null);
          setDeleteFieldLogicJumps([]);
        }}
      />
      <form
        onSubmit={handleSubmit(onSave, onError)}
        className={cn(styles.root, 'card', 'card-with-border')}>
        <div>
          <div className="u-padding--large">
            <div className={cn('u-flex-box', 'u-flex-justify-between', 'u-flex-align-center')}>
              <div className="u-width-50">
                {!form.team_id && <Badge className="u-margin-bottom--small">Global</Badge>}
                <div className={cn('u-flex-box', 'u-flex-align-center')}>
                  <EditableTitle
                    register={register}
                    name="name"
                    value={nameValue}
                    disabled={readOnly}
                  />
                </div>
                <p className="t-small">Edit your form below</p>
              </div>
              <div className="d-flex align-items-center">
                {!form.archived && (
                  <>
                    {company?.ff_ai_forms && aiGenerators && (
                      <>
                        <IconButton
                          className="u-margin-right--small"
                          onClick={onAIQuestionnaireGenerator}
                          icon="fa-solid fa-file-pdf"
                          prefix="fa"
                          tip="Generate from PDF"
                        />
                        <IconButton
                          className="u-margin-right--small"
                          onClick={onJDAIGenerator}
                          icon="robot"
                          prefix="fa"
                          tip="Generate from Job Description"
                        />
                      </>
                    )}
                    <IconComponent
                      icon={PlayIcon}
                      type="secondary"
                      border={false}
                      noBackground
                      strokeWidth={1.5}
                      size="large"
                      tip="Preview Form"
                      onClick={() => setShowPreview(true)}
                    />
                    {form.id && (
                      <IconComponent
                        icon={Copy01Icon}
                        type="secondary"
                        border={false}
                        noBackground
                        strokeWidth={1.5}
                        size="medium"
                        tip="Make a copy"
                        onClick={() => onCopy(form)}
                      />
                    )}
                    <IconComponent
                      icon={Archive03Icon}
                      type="secondary"
                      border={false}
                      noBackground
                      strokeWidth={1.5}
                      size="medium"
                      tip="Archive Form"
                      onClick={handleArchive}
                    />
                  </>
                )}
                {form.id && (
                  <IconComponent
                    icon={Delete02Icon}
                    type="error"
                    border={false}
                    noBackground
                    strokeWidth={1.5}
                    size="medium"
                    tip="Delete Form"
                    onClick={handleDelete}
                  />
                )}
                {form.archived && (
                  <IconComponent
                    icon={RestoreBinIcon}
                    type="secondary"
                    border={false}
                    noBackground
                    strokeWidth={1.5}
                    size="medium"
                    tip="Restore Form"
                    onClick={handleRestore}
                  />
                )}
              </div>
            </div>
            <EditableSettings
              title="Settings"
              className="u-margin-top u-margin-bottom--0"
              startOpen={true}>
              <InputGroup title="Team">
                <Select
                  name="team_id"
                  placeholder="Team"
                  register={register}
                  value={watchTeam}
                  useDefault={!hasAccess(Permissions.TeamsRead)}>
                  {hasAccess(Permissions.TeamsRead) && (
                    <Select.Item value={null}>Global</Select.Item>
                  )}
                  {teams.map(team => (
                    <Select.Item key={team.id} value={team.id}>
                      {team.name}
                    </Select.Item>
                  ))}
                </Select>
              </InputGroup>
              {form.type == FormTypes.CANDIDATE && (
                <>
                  <InputGroup title="Description">
                    <Textarea
                      name="description"
                      register={register}
                      placeholder="Desciption..."
                      inputProps={{ rows: 3 }}
                    />
                  </InputGroup>
                  <ToggleSwitchInput
                    text="Has Introduction Statement Field"
                    noMaxWidth
                    subtext="Enable this to add an introductory statement at the start of your form. This field appears before all other questions and cannot be reordered, duplicated, or have logic applied. You can delete it and re-enable it anytime by toggling this switch."
                    onChange={e => handleHasIntroField(e.target.checked)}
                    value={hasIntroField}
                  />
                </>
              )}
            </EditableSettings>
          </div>
          <Tabs defaultActiveKey="questions" className="tabs no-padding padding-small">
            <Tab eventKey="questions" title="Questions">
              <div className={cn('surface-secondary u-padding', readOnly && 'u-padding--large')}>
                <CollapsibleGroup newStartOpen={true}>
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="fields">
                      {({ innerRef, droppableProps, placeholder }) => (
                        <div className="u-width-100" {...droppableProps} ref={innerRef}>
                          {fields.map((field, index) => (
                            <FieldConfig
                              key={field.key}
                              index={index}
                              formField={field}
                              logicJumpsCount={getLogicJumpsCount(field?.order)}
                              register={register}
                              watch={watch}
                              control={control}
                              errors={errors?.fields?.[index]}
                              remove={handleDeleteField}
                              duplicate={duplicateField}
                              copyField={copyField}
                              pasteFieldBelow={pasteFieldBelow}
                              pasteFieldAbove={pasteFieldAbove}
                              unregister={unregister}
                              pasteEnabled={!!copyFieldDetails}
                              setValue={setValue}
                            />
                          ))}
                          {placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </CollapsibleGroup>
                {!readOnly && (
                  <Button
                    className="u-width-100 u-margin-top"
                    type="secondary-reverse"
                    dataTestId={`${dataTestId}-add-field`}
                    onClick={addField}>
                    <FeatherIcon size={18} className="u-margin-right" icon="plus" />
                    Add Field
                  </Button>
                )}
              </div>
            </Tab>
            <Tab eventKey="logic" title="Logic">
              <div
                className={cn(
                  'background-secondary u-padding',
                  readOnly && 'u-border-bottom-radius',
                )}>
                <CollapsibleGroup newStartOpen={true}>
                  <div className="u-width-100">
                    {logicJumps?.map((_, index) => (
                      <LogicConfig
                        key={index}
                        index={index}
                        blockBefore={blockBeforeFields}
                        control={control}
                        register={register}
                        onDelete={removeLogic}
                        locale={locale}
                      />
                    ))}
                  </div>
                </CollapsibleGroup>
                {!readOnly && (
                  <Button
                    className="u-width-100 u-margin-top"
                    type="secondary-reverse"
                    onClick={() => appendLogic({})}>
                    <FeatherIcon size={18} className="u-margin-right" icon="plus" />
                    Add Logic Jump
                  </Button>
                )}
              </div>
            </Tab>
          </Tabs>
          {!readOnly && (
            <div className={cn('card_footer', 'u-flex-box', 'u-flex-justify-between')}>
              <Button onClick={onClose} type="secondary">
                Cancel
              </Button>
              <Button submit dataTestId={`${dataTestId}-save-form`}>
                Save
              </Button>
            </div>
          )}
        </div>
      </form>
    </>
  );
};

export const FieldConfig = ({
  formField,
  index,
  register,
  control,
  watch,
  duplicate,
  remove,
  unregister,
  logicJumpsCount,
  errors,
  copyField,
  pasteFieldBelow,
  pasteFieldAbove,
  pasteEnabled,
  setValue,
}) => {
  const registerName = `fields.${index}`;
  const { fields } = useFieldArray({ control, name: 'fields' });

  const field = useWatch({ control, name: `fields.${index}` });
  const typeValue = useWatch({ control, name: `${registerName}.type` });

  useEffect(() => {
    if (typeValue === FieldTypes.MultipleChoice) return;
    if (typeValue === FieldTypes.Statement) {
      // Keep the existing text value when switching to Statement
      const currentText = field?.text;
      if (currentText) {
        setValue(`${registerName}.text`, currentText);
      }
    }
    unregister(`${registerName}.options.options`);
  }, [typeValue, index, field?.text]);

  const actions = useMemo(() => {
    return [
      {
        title: 'Copy field',
        icon: Copy02Icon,
        onClick: () => copyField(field),
      },
      {
        title: 'Paste field above',
        icon: SquareArrowUpDoubleIcon,
        onClick: () => pasteFieldAbove(field),
        disabled: !pasteEnabled,
      },
      {
        title: 'Paste field below',
        icon: SquareArrowDownDoubleIcon,
        onClick: () => pasteFieldBelow(field),
        disabled: !pasteEnabled,
      },
    ];
  }, [formField, pasteEnabled]);

  const renderSettings = () => {
    if (field.locked) return;

    switch (field?.type) {
      case FieldTypes.MultipleChoice:
        return (
          <MultipleChoiceSettings
            register={register}
            name={registerName}
            field={field}
            control={control}
            errors={errors}
          />
        );
      case FieldTypes.OpinionScale:
        return (
          <OpinionScaleSettings
            register={register}
            name={registerName}
            field={field}
            watch={watch}
          />
        );
      case FieldTypes.StarRating:
        return (
          <StarRatingSettings register={register} name={registerName} field={field} watch={watch} />
        );
      case FieldTypes.Date:
        return <DateSettings register={register} name={registerName} field={field} />;
      case FieldTypes.Download:
        return (
          <DownloadSettings
            register={register}
            name={registerName}
            field={field}
            setValue={setValue}
            control={control}
            errors={errors}
          />
        );
      case FieldTypes.Statement:
        return null;
      default:
        return <DefaultSettings register={register} name={registerName} field={field} />;
    }
  };

  return (
    <Draggable
      draggableId={formField.key}
      index={index}
      isDragDisabled={field.locked || field.options?.intro}>
      {({ draggableProps, dragHandleProps, innerRef }) => (
        <div {...draggableProps} {...dragHandleProps} ref={innerRef}>
          <CollapsibleBlock className="u-margin-bottom u-drop-shadow--xs" keyCode={`${index}`}>
            <CollapsibleBlock.Header alignStart={true}>
              <div className={cn(styles.questionTypeContainer, 'd-flex align-items-center u-gap')}>
                <IconComponent
                  icon={DragDropVerticalIcon}
                  type="neutral-light"
                  border={false}
                  noBackground
                  strokeWidth={4}
                  size="large"
                />
                <span
                  className={cn(
                    styles.order,
                    'white-text u-border-radius--large surface-primary-dark flex-grow-1 u-margin-right--small',
                  )}>
                  {field.order}
                </span>
              </div>
              <div className={styles.textContainer}>
                <Select
                  rounded
                  register={register}
                  disabled={field.locked || field.options?.intro}
                  name={`${registerName}.type`}
                  className={styles.typeSelector}
                  value={field.type}>
                  {FieldTypesOptions.map(fieldType => (
                    <Select.Item
                      key={fieldType.key}
                      value={fieldType.key}
                      description={fieldType.subTitle}>
                      {fieldType.title}
                    </Select.Item>
                  ))}
                </Select>
                {field.type === FieldTypes.Statement ? (
                  <Controller
                    control={control}
                    name={`${registerName}.text`}
                    rules={{
                      required: 'This field is required.',
                      validate: value => {
                        if (!value) return 'This field is required.';
                        const plainText = value.replace(/<[^>]+>/g, '').trim();
                        return plainText.length > 0 || 'This field is required.';
                      },
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <RichTextEditor
                        onChange={onChange}
                        value={value}
                        className={styles.textInputRichText}
                        error={error}
                      />
                    )}
                  />
                ) : (
                  <Textarea
                    register={register}
                    name={`${registerName}.text`}
                    validators={{ required: true }}
                    error={errors?.text?.type}
                    noErrorMessage
                    placeholder={field.placeholder || 'Enter question...'}
                    className={styles.textInput}
                    dynamicHeight={true}
                  />
                )}
              </div>
              <div className={cn(styles.validationContainer, 'u-margin-top--small')}>
                <StatusPill
                  text={field.required ? 'Required' : 'Optional'}
                  status={field.required ? 'info' : 'neutral'}
                />
              </div>
              <div className={styles.iconContainer}>
                {field.locked ? (
                  <IconComponent
                    icon={SquareLock02Icon}
                    type="neutral-light"
                    border={false}
                    noBackground
                    strokeWidth={1.5}
                    size="medium"
                  />
                ) : (
                  <div className={cn(styles.iconContainer, 'd-flex', 'align-items-center')}>
                    {!field.options?.intro && (
                      <>
                        <IconComponent
                          tip={`${logicJumpsCount > 0 ? `${logicJumpsCount} logic jumps` : 'No logic jumps'}`}
                          noBackground
                          type="secondary"
                          size="medium"
                          strokeWidth={1.5}
                          {...(logicJumpsCount > 0 && { superfix: logicJumpsCount })}
                          icon={GitBranchIcon}
                        />
                        <IconComponent
                          tip="Duplicate Field"
                          onClick={() => duplicate(field)}
                          noBackground
                          type="secondary"
                          size="medium"
                          strokeWidth={1.5}
                          icon={Copy01Icon}
                        />
                      </>
                    )}
                    <IconComponent
                      tip="Delete Field"
                      onClick={() => remove(index)}
                      type="error"
                      noBackground
                      size="medium"
                      strokeWidth={1.5}
                      icon={Delete02Icon}
                    />
                    {!field.options?.intro && (
                      <ActionsMenu newIconLibrary={true}>
                        {actions.map((action, index) =>
                          action.hide ? null : (
                            <ActionsMenu.Item
                              newIconLibrary={true}
                              key={action.title + index}
                              icon={action.icon}
                              title={action.title}
                              onClick={() => action.onClick(field)}
                              disabled={action.disabled}
                            />
                          ),
                        )}
                      </ActionsMenu>
                    )}
                  </div>
                )}
              </div>
            </CollapsibleBlock.Header>
            <CollapsibleBlock.Content>{renderSettings()}</CollapsibleBlock.Content>
          </CollapsibleBlock>
        </div>
      )}
    </Draggable>
  );
};

const LogicConfig = ({
  control,
  blockBefore = 0,
  index,
  register,
  onDelete = () => {},
  locale = 'en_GB',
}) => {
  const watchFields = useWatch({ control, name: 'fields' });
  const logicField = useWatch({ control, name: `logic_jumps.${index}` });

  const operator = (operatorTypes || [])?.find(
    operatorType => operatorType.value === logicField?.operator,
  );

  return (
    <CollapsibleBlock className={cn(styles.logicField, 'u-margin-bottom')} keyCode={`${index}`}>
      <CollapsibleBlock.Header>
        <span className={styles.logicFieldHeader}>
          If <b>({logicField.field})</b> is <b>{operator?.label}</b> <b>"{logicField.value}"</b>,
          then jump from <b>({logicField.from})</b> to <b>({logicField.to})</b>
        </span>
        <IconButton
          className={styles.logicFieldRemoveButton}
          icon="trash-2"
          onClick={() => onDelete(index)}
        />
      </CollapsibleBlock.Header>
      <CollapsibleBlock.Content>
        <LogicSettings
          index={index}
          fields={watchFields}
          blockBefore={blockBefore}
          control={control}
          register={register}
          locale={locale}
        />
      </CollapsibleBlock.Content>
    </CollapsibleBlock>
  );
};
